import { useCallback, useEffect, useState } from "react";

export type DecayFormula = (currentLevel: number, deltaTime: number) => number;

// Pre-defined formulas
export const Formulas = {
	Linear:
		(rate: number): DecayFormula =>
		(level, dt) =>
			level - rate * dt,
	Exponential:
		(factor: number): DecayFormula =>
		(level, dt) =>
			level * factor ** dt,
	Steady: (): DecayFormula => (level) => level, // No decay
};

export interface PhaseActivationProps {
	init_level: number;
	max_level: number;
	decayFn: DecayFormula;
	on_triggered?: () => void;
}

/**
 * Hook to track activation level with a decay function.
 *
 * Once level reaches max_level, it triggers the callback and remains in triggered state
 * until clr() is called.
 */
export const usePhaseActivation = ({
	init_level,
	max_level,
	decayFn,
	on_triggered,
}: PhaseActivationProps) => {
	const [level, setLevel] = useState(init_level);
	const [isTriggered, setTriggered] = useState(false);

	useEffect(() => {
		if (isTriggered) return;

		const interval = setInterval(() => {
			setLevel((prev) => {
				// Apply the passed-in formula
				// dt is 0.1 because interval is 100ms
				const next = decayFn(prev, 0.1);
				return Math.max(0, Math.min(max_level, next));
			});
		}, 2000);

		return () => clearInterval(interval);
	}, [isTriggered, decayFn, max_level]);

	const incr = useCallback(
		(amount: number) => {
			setLevel((prev) => {
				if (prev >= max_level) return prev;

				const candidate = Math.min(prev + amount, max_level);

				if (candidate === max_level && !isTriggered) {
					setTriggered(true);
					on_triggered?.();
				}

				return candidate;
			});
		},
		[max_level, isTriggered, on_triggered],
	);

	const clr = useCallback(() => {
		setTriggered(false);
		setLevel(0);
	}, []);

	const progress = (level / max_level) * 100;

	return {
		level,
		progress,
		isTriggered,
		incr,
		clr,
	};
};
